import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { Modal } from "../../components/Modal";
import { StatusMark } from "../../components/StatusMark";
import { formatDate, formatTime, currency, formatApiErrorDetail } from "../../lib/format";

const EMPTY = {
  title: "", date: "", start_time: "19:00", end_time: "22:00", venue: "",
  track: "Independent", capacity: 25, rate_per_student: 40, status: "scheduled",
  admin_label: "", color: "", open_for_signups: false,
};

const COLOR_TOKENS = ["blue", "sky", "teal", "plum", "violet", "olive"];

function ClassForm({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
  }, [initial, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        capacity: Number(form.capacity),
        rate_per_student: Number(form.rate_per_student),
        open_for_signups: !!form.open_for_signups,
        admin_label: form.admin_label || "",
        ...(form.color ? { color: form.color } : {}),
      };
      if (!payload.color) delete payload.color;
      if (initial?.id) await api.put(`/classes/${initial.id}`, payload);
      else await api.post("/classes", payload);
      toast.success(initial?.id ? "Class updated." : "Class created.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial?.id ? "Edit class" : "New class"}
      testid="class-form-modal"
      footer={
        <>
          <button onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-brass text-sm" data-testid="class-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="field-label">Title</label>
          <input className="field-input" value={form.title} onChange={set("title")} data-testid="class-title" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="field-label">Date</label>
            <input type="date" className="field-input" value={form.date} onChange={set("date")} data-testid="class-date" />
          </div>
          <div>
            <label className="field-label">Venue</label>
            <input className="field-input" value={form.venue} onChange={set("venue")} data-testid="class-venue" />
          </div>
          <div>
            <label className="field-label">Start</label>
            <input type="time" className="field-input" value={form.start_time} onChange={set("start_time")} data-testid="class-start" />
          </div>
          <div>
            <label className="field-label">End</label>
            <input type="time" className="field-input" value={form.end_time} onChange={set("end_time")} data-testid="class-end" />
          </div>
          <div>
            <label className="field-label">Track</label>
            <select className="field-input" value={form.track} onChange={set("track")} data-testid="class-track">
              <option value="Independent">Independent</option>
              <option value="TVI">TVI</option>
            </select>
          </div>
          <div>
            <label className="field-label">Status</label>
            <select className="field-input" value={form.status} onChange={set("status")} data-testid="class-status">
              <option value="scheduled">Scheduled</option>
              <option value="taught">Taught</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="field-label">Capacity</label>
            <input type="number" className="field-input" value={form.capacity} onChange={set("capacity")} data-testid="class-capacity" />
          </div>
          <div>
            <label className="field-label">Rate / student ($)</label>
            <input type="number" className="field-input" value={form.rate_per_student} onChange={set("rate_per_student")} data-testid="class-rate" />
          </div>
          <div>
            <label className="field-label">Admin label (group pill — never public)</label>
            <input className="field-input" value={form.admin_label || ""} onChange={set("admin_label")} placeholder="e.g. Oct 4 Scene Study — Whitefire" data-testid="class-admin-label" />
          </div>
          <div>
            <label className="field-label">Group colour</label>
            <select className="field-input" value={form.color || ""} onChange={set("color")} data-testid="class-color">
              <option value="">Auto (assigned at creation)</option>
              {COLOR_TOKENS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <label className="flex items-center gap-2 font-sans text-sm text-ink cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.open_for_signups}
            onChange={(e) => setForm((f) => ({ ...f, open_for_signups: e.target.checked }))}
            data-testid="class-open-for-signups"
          />
          Open for /join signups
          <span className="font-mono text-xs text-stone">({form.date || "no date set"})</span>
        </label>
      </div>
    </Modal>
  );
}

export default function ReefClasses() {
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/classes");
    setClasses(r.data);
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  return (
    <div className="space-y-8" data-testid="reef-classes">
      <div className="flex items-end justify-between">
        <div>
          <p className="overline mb-2">Each class is a shoot day</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Classes</h1>
        </div>
        <button onClick={() => { setEditing(null); setOpen(true); }} className="btn-brass text-sm" data-testid="new-class-btn">
          <Plus size={16} /> New class
        </button>
      </div>

      <div className="space-y-4">
        {classes.map((c) => (
          <div key={c.id} className="paper-card p-6" data-testid={`reef-class-row-${c.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="font-serif text-2xl text-ink leading-tight">{c.title}</h3>
                  <StatusMark status={c.status} />
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 font-mono text-sm text-stone">
                  <span>{formatDate(c.date)}</span>
                  <span>{formatTime(c.start_time)}–{formatTime(c.end_time)}</span>
                  <span>{c.track}</span>
                  <span>{currency(c.rate_per_student)}/head</span>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="font-mono text-2xl text-ink">{c.confirmed_count}/{c.capacity}</p>
                  <p className="font-sans text-[10px] uppercase tracking-wider text-stone">
                    cast · {c.open_seats} open {c.pending_count > 0 ? `· ${c.pending_count} pending` : ""}
                  </p>
                </div>
                <button onClick={() => { setEditing(c); setOpen(true); }} className="btn-ghost text-sm" data-testid={`edit-class-${c.id}`}>
                  <Pencil size={15} />
                </button>
                <Link to={`/reef/classes/${c.id}`} className="btn-outline text-sm" data-testid={`open-class-${c.id}`}>
                  Call sheet <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </div>
        ))}
        {classes.length === 0 && <p className="font-sans text-sm text-stone">No classes yet.</p>}
      </div>

      <ClassForm open={open} onClose={() => setOpen(false)} initial={editing} onSaved={load} />
    </div>
  );
}
